import cx from "classnames";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import React, { useEffect } from "react";

import { CartFreeShippingProgress } from "@/components/CartFreeShippingProgress";
import { CartLineItem } from "@/components/CartLineItem";
import { Button } from "@/components/elements/Button";
import { Cta, CtaSize, CtaTheme } from "@/components/elements/Cta";
import { Icon } from "@/components/elements/Icon";
import { Text } from "@/components/elements/Text";
import { PaddingWrapper } from "@/components/PaddingWrapper";
import { LINKS } from "@/constants/links";
import { useProfile } from "@/hooks/profile";
import { useAccountType } from "@/hooks/useAccountType/useAccountType";
import { CartItem, CartItemCategory, useCart } from "@/hooks/useCart";
import useGlobalState from "@/hooks/useGlobalState";
import { useProducts } from "@/hooks/useProducts";
import useRemoveScrollBar from "@/hooks/useRemoveScrollBar";
import { calculateCartTotals, getItemCount } from "@/utils/cart";
import { formatCurrency } from "@/utils/format";
import { findRecommendationsBasedOnOrderHistory } from "@/utils/order-history";

import { InactiveProductWarning } from "./components/InactiveProductWarning";
import { OutOfStockProductWarnings } from "./components/OutOfStockProductWarnings";
import styles from "./styles.module.css";

export const CartContents = () => {
  const { data: productsData } = useProducts();

  const profileQuery = useProfile();
  const { isBusinessAccount } = useAccountType();
  const { t } = useTranslation();
  const { reinstateScrollBar } = useRemoveScrollBar();
  const { toggleCartIsOpen } = useGlobalState();
  const { cart, updateItem, removeItem } = useCart();

  const totalProducts = getItemCount(cart.products);
  const { grand_total, shipping_price } = calculateCartTotals(cart);

  const { events, locale } = useRouter();
  const { data: profile } = profileQuery;

  const filteredOrderHistory = findRecommendationsBasedOnOrderHistory(
    profile?.order_history || [],
    cart.products
  );

  // For when cart opened 'on top of' menu
  // and checkout clicked
  // resulting in no scrollbar
  function onRouteChangeStart(route: string) {
    if (route === `/${locale}${LINKS.CHECKOUT}`) reinstateScrollBar();
  }

  useEffect(() => {
    events.on("routeChangeStart", onRouteChangeStart);

    return () => {
      events.off("routeChangeStart", onRouteChangeStart);
    };
  }, [events]);

  const productsById = productsData?.productsById || {};

  const onChange = (item: CartItem, category: CartItemCategory) => {
    updateItem(item, category);
  };

  const onRemove = (id: number) => {
    removeItem(id);
  };

  return (
    <div className={cx(styles.cart)} data-testid="cart">
      <PaddingWrapper className={styles.cartInner}>
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.headerTopRow}>
            <Text
              as="h3"
              size="m"
              responsive
              className={styles.title}
              disableMarginBottom
            >
              {t("shopping_cart_title")}
              {
                " " /* This space before total products is to make the text read "Cart 1" instead of "Cart1" */
              }
              <span className={styles.itemCount}>{totalProducts}</span>
            </Text>

            <button
              onClick={() => {
                toggleCartIsOpen(false);
              }}
              aria-label={t("close_cart_CTA")}
              className={styles.closeButton}
            >
              <Icon type="close" />
            </button>
          </div>
          {cart.products.length > 0 && !isBusinessAccount && (
            <CartFreeShippingProgress className={styles.freeShippingProgress} />
          )}
          <div className={styles.headerLine} />
        </div>

        <div
          className={cx(styles.cartContents, {
            [styles.hasItems]:
              cart.products.length ||
              findRecommendationsBasedOnOrderHistory.length,
          })}
        >
          {/* OUT OF STOCK INFO BOXES */}
          <OutOfStockProductWarnings />

          {/* INACTIVE PRODUCTS INFO BOXES */}
          <InactiveProductWarning />

          {/* LIST OF PRODUCTS or EMPTY CART MESSAGE */}
          {cart.products.length > 0 ? (
            <section className={styles.itemsSection}>
              <ul className={styles.itemsList}>
                {cart.products.map((item) => {
                  return (
                    <CartLineItem
                      key={item.id}
                      item={item}
                      version="editable"
                      className={styles.lineItem}
                      onChange={(value) =>
                        onChange(
                          {
                            id: item.id,
                            price: item.price,
                            quantity: value,
                          },
                          "products"
                        )
                      }
                      onRemove={() => onRemove(item.id)}
                    />
                  );
                })}
              </ul>
            </section>
          ) : (
            <Text size="xs" className={styles.emptyCartMessage}>
              {t("cart_confirmation_fallback")}
            </Text>
          )}

          {/* HOME STOCK (IF ANY) */}
          {filteredOrderHistory.length > 0 && (
            <section className={styles.homeStockSection}>
              <Text as="h3" className={styles.homeStockTitle}>
                {t("shopping_cart_stock_title")}
              </Text>
              <Text size="xxs" className={styles.homeStockSubtitle}>
                {t("shopping_cart_stock_description")}
              </Text>
              <ul className={styles.homeStockList}>
                {filteredOrderHistory.map(
                  ({
                    product: { id: product_id },
                    last_ordered: dateRefilled,
                    quantity,
                  }) => {
                    const item = {
                      ...productsById[product_id],
                      quantity: 1,
                    };
                    return (
                      <CartLineItem
                        key={item.id}
                        item={item}
                        version="homeStockCart"
                        dateRefilled={dateRefilled}
                        quantityRefilled={quantity}
                        onChange={(value) =>
                          onChange(
                            {
                              id: item.id,
                              price: item.price,
                              quantity: value,
                            },
                            "products"
                          )
                        }
                        onRemove={() => onRemove(item.id)}
                      />
                    );
                  }
                )}
              </ul>
            </section>
          )}

          {/* SHOP BUTTON if not home products */}
          <div className={styles.shopButtonContainer}>
            <Button pageLink="SHOP" theme="outline-square" width="full">
              {t("shopping_cart_view_more")}
            </Button>
          </div>
        </div>
      </PaddingWrapper>
      {/* CHECKOUT BUTTON CONTAINER */}
      {!!cart.products?.length && (
        <PaddingWrapper className={styles.checkoutContainer} onlyX>
          <div>
            <Cta
              theme={CtaTheme.DARK}
              size={CtaSize.REGULAR}
              as="a"
              id="checkout-link"
              href={`/${locale}${LINKS.CHECKOUT}`}
              className={styles.checkout}
            >
              {t("checkout_title")} -{" "}
              {formatCurrency(
                isBusinessAccount ? grand_total - shipping_price : grand_total
              )}
            </Cta>
          </div>
          {!isBusinessAccount ? (
            <Text size="xxs" className={styles.checkoutContainerCopy}>
              {shipping_price
                ? t("shopping_cart_shipping_fee", {
                    price: formatCurrency(shipping_price),
                  })
                : t("shopping_cart_free_shipping")}
            </Text>
          ) : null}
        </PaddingWrapper>
      )}
    </div>
  );
};
